import { supabase } from '@/services/supabase';
import { 
  CACHE_KEYS,
  clearCourseCache,
  syncCourseCache
} from '@/utils/cacheService';
import { Category, ContentType } from './categoryService';
import { filterCategoriesByContentType } from '@/utils/categoryUtils';

// Định nghĩa kiểu dữ liệu cho Course
export interface Course {
  id: string;
  name: string;
  category_id: string; // Thay đổi từ category string sang category_id để liên kết với bảng categories
  difficulty: string;
  description: string;
  tags: string[];
  image: string;
  startnodeid: string;
  price: number; // Thêm trường price để lưu giá khóa học
  estimated_time?: string; // Thời gian ước tính để hoàn thành khóa học
  expert_id?: string; // ID của chuyên gia được gán cho khóa học
  created_at?: string;
  updated_at?: string;
  category?: Category; // Thêm trường category để lưu thông tin danh mục đầy đủ
  status?: 'draft' | 'published' | 'archived'; // Thêm trường status để quản lý trạng thái
}

// Định nghĩa kiểu dữ liệu cho CourseContent
export interface CourseContent {
  id: string;
  course_id: string;
  content: any; // JSON content
  created_at?: string;
  updated_at?: string;
}

// Thêm khai báo cache để lưu trữ tạm dữ liệu
const courseCache: { [key: string]: Course } = {};
const contentCache: { [key: string]: CourseContent } = {};

// Hàm lấy dữ liệu từ cache theo ID
const getCourseCache = (id: string): Course | null => {
  return courseCache[id] || null;
};

// Hàm lấy nội dung khóa học từ cache
const getCourseContentCache = (courseId: string): CourseContent | null => {
  return contentCache[courseId] || null;
};

// Cập nhật hàm setCourseCache để lưu vào cache local
export const setCourseCache = (id: string, data: Course) => {
  courseCache[id] = data;
};

// Cập nhật hàm setCourseContentCache để lưu vào cache local
export const setCourseContentCache = (data: CourseContent, courseId?: string) => {
  const id = courseId || data.course_id;
  contentCache[id] = data;
};

// Lấy tất cả khóa học
export const getAllCourses = async (): Promise<Course[]> => {
  const { data, error } = await supabase
    .from('courses')
    .select('*, category:category_id(*)')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching courses:', error);
    throw error;
  }

  // Đảm bảo trường price luôn là số
  const processedData = data?.map(course => {
    const processedCourse = {
      ...course,
      price: typeof course.price === 'number' ? course.price : 0
    };
    
    // console.log(`getAllCourses - course ${course.id} price:`, course.price, typeof course.price);
    // console.log(`getAllCourses - processed ${processedCourse.id} price:`, processedCourse.price, typeof processedCourse.price);
    
    return processedCourse;
  }) || [];

  return processedData;
};

// Lấy khóa học theo ID
export const getCourseById = async (id: string): Promise<Course | null> => {
  try {
    console.log(`Fetching course with ID ${id}`);
    
    // Ưu tiên lấy từ cache trước
    const cachedCourse = getCourseCache(id);
    if (cachedCourse) {
      console.log(`Found course ${id} in cache`);
      return cachedCourse;
    }
    
    const { data, error } = await supabase
      .from('courses')
      .select('*, category:category_id(*)')
      .eq('id', id)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        console.log(`No course found with ID ${id}, checking if this is an AI-generated course...`);
        
        // Kiểm tra nếu đây là ID của một khóa học được tạo bởi AI
        // Thường các khóa học AI có ID dạng "course-xxxx"
        if (id.startsWith('course-')) {
          // Kiểm tra xem có dữ liệu trong localStorage không
          try {
            // Kiểm tra trong localStorage xem có khóa học AI generate
            const aiGeneratedContent = localStorage.getItem(`ai_generated_course_${id}`);
            if (aiGeneratedContent) {
              const parsedContent = JSON.parse(aiGeneratedContent);
              // Trả về dữ liệu khóa học từ localStorage
              console.log(`Found AI-generated course ${id} in localStorage`);
              return parsedContent;
            }
            
            // Thêm kiểm tra trường hợp đặc biệt: AI-generated trong local storage với key khác
            const previewContent = localStorage.getItem(`preview_course_${id}`);
            if (previewContent) {
              const parsedContent = JSON.parse(previewContent);
              console.log(`Found preview course ${id} in localStorage`);
              return parsedContent;
            }
            
            // Tìm kiếm trong tất cả các key của localStorage có chứa courseId
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && key.includes(id)) {
                try {
                  const storedData = localStorage.getItem(key);
                  if (storedData) {
                    const parsedData = JSON.parse(storedData);
                    // Kiểm tra xem object có id khớp với courseId không
                    if (parsedData && parsedData.id === id) {
                      console.log(`Found course ${id} in localStorage with key: ${key}`);
                      return parsedData;
                    }
                  }
                } catch (e) {
                  console.error(`Error parsing localStorage data with key ${key}:`, e);
                }
              }
            }
          } catch (e) {
            console.error('Error checking localStorage for AI-generated course:', e);
          }
        }
      }
      console.error(`Error fetching course with ID ${id}:`, error);
      return null;
    }
    
    // Cache lại kết quả
    setCourseCache(id, data);
    
    return data;
  } catch (error) {
    console.error(`Error in getCourseById for ${id}:`, error);
    return null;
  }
};

// Lấy nội dung khóa học theo course_id
export const getCourseContent = async (courseId: string): Promise<CourseContent | null> => {
  try {
    console.log('getCourseContent được gọi với courseId:', courseId);
    
    // Kiểm tra cache trước
    const cachedContent = getCourseContentCache(courseId);
    if (cachedContent) {
      console.log(`Found course content for ${courseId} in cache`);
      return cachedContent;
    }
    
    // Thử tìm khóa học AI-generated từ localStorage
    if (courseId.startsWith('course-')) {
      try {
        // Kiểm tra các key có thể chứa nội dung khóa học AI
        const possibleKeys = [
          `ai_generated_course_${courseId}`,
          `preview_course_${courseId}`,
          `preview_course_content_${courseId}`,
          `course-${courseId}`
        ];
        
        for (const key of possibleKeys) {
          const storedData = localStorage.getItem(key);
          if (storedData) {
            try {
              const parsedData = JSON.parse(storedData);
              // Kiểm tra xem object có id khớp với courseId và có nodes không
              if (parsedData && parsedData.id === courseId && Array.isArray(parsedData.nodes)) {
                console.log(`Found AI-generated course content for ${courseId} in localStorage with key: ${key}`);
                // Kiểm tra nếu data đã có đúng cấu trúc course content
                if (!parsedData.course_id && !parsedData.content) {
                  // Cần convert về định dạng course_content
                  const formattedContent: CourseContent = {
                    id: `content-${courseId}`, // Tạo id content
                    course_id: courseId,
                    content: parsedData // Lưu toàn bộ nội dung khóa học AI vào content
                  };
                  return formattedContent;
                }
                return parsedData;
              }
            } catch (e) {
              console.error(`Error parsing localStorage data with key ${key}:`, e);
            }
          }
        }
      } catch (e) {
        console.error('Error checking localStorage for AI course content:', e);
      }
    }
    
    const { data, error } = await supabase
      .from('course_contents')
      .select('*')
      .eq('course_id', courseId);

    if (error) {
      console.error(`Error fetching course content for course ${courseId}:`, error);
      throw error;
    }

    console.log(`Kết quả từ query course_contents với course_id=${courseId}:`, data);

    // Cache kết quả
    if (data && data.length > 0) {
      setCourseContentCache(data[0]);
    }
    
    // Trả về bản ghi đầu tiên nếu có, hoặc null nếu không có bản ghi nào
    return data && data.length > 0 ? data[0] : null;
  } catch (error) {
    console.error(`Error fetching course content for course ${courseId}:`, error);
    // Trả về null thay vì throw error để component không bị crash
    return null;
  }
};

// Tạo khóa học mới
export const createCourse = async (course: Omit<Course, 'created_at' | 'updated_at' | 'category'>): Promise<Course> => {
  console.log('Creating course with data:', course);
  
  const now = new Date().toISOString();
  const newCourse = {
    ...course,
    // Thêm trường category (bắt buộc) - sử dụng category_id hoặc giá trị mặc định
    category: course.category_id || '00000000-0000-0000-0000-000000000000',
    status: course.status || 'draft', // Mặc định là draft khi tạo mới
    created_at: now,
    updated_at: now
  };

  console.log('Sending to Supabase:', newCourse);
  
  const { data, error } = await supabase
    .from('courses')
    .insert(newCourse)
    .select('*, category:category_id(*)')
    .single();

  if (error) {
    console.error('Error creating course:', error);
    throw error;
  }

  // Cập nhật cache
  clearCourseCache(CACHE_KEYS.COURSES);
  
  return data;
};

// Cập nhật khóa học
export const updateCourse = async (id: string, course: Partial<Omit<Course, 'category'>>, isUpsert: boolean = false): Promise<Course> => {
  console.log(`Updating course with id ${id}:`, course);
  
  // Kiểm tra xem khóa học đã tồn tại chưa
  if (isUpsert) {
    const { data: existingCourse } = await supabase
      .from('courses')
      .select('id')
      .eq('id', id)
      .maybeSingle();
    
    // Nếu khóa học không tồn tại và isUpsert = true, tạo mới
    if (!existingCourse) {
      console.log(`Course with id ${id} not found, creating new course (upsert)`);
      
      const now = new Date().toISOString();
      const newCourse = {
        id,
        name: course.name || 'Khóa học mới',
        category_id: course.category_id || '00000000-0000-0000-0000-000000000000',
        // Thêm trường category (bắt buộc)
        category: course.category_id || '00000000-0000-0000-0000-000000000000',
        difficulty: course.difficulty || 'beginner',
        description: course.description || '',
        tags: course.tags || ['course'],
        image: course.image || '',
        startnodeid: course.startnodeid || '',
        price: course.price || 0,
        status: course.status || 'draft', // Thêm status với giá trị mặc định
        created_at: now,
        updated_at: now
      };
      
      const { data, error } = await supabase
        .from('courses')
        .insert(newCourse)
        .select('*, category:category_id(*)')
        .single();
      
      if (error) {
        console.error(`Error creating course during upsert with id ${id}:`, error);
        throw error;
      }
      
      // Cập nhật cache
      clearCourseCache(CACHE_KEYS.COURSES);
      
      return data;
    }
  }
  
  // Cập nhật khóa học nếu đã tồn tại hoặc không phải upsert
  const now = new Date().toISOString();
  
  // Tạo bản sao của đối tượng course để tránh làm thay đổi đối tượng gốc
  const updates = {
    ...course,
    // Thêm trường category nếu có category_id trong dữ liệu cập nhật
    ...(course.category_id ? { category: course.category_id } : {}),
    updated_at: now
  };
  
  console.log('Sending updates to server:', updates);
  
  const { data, error } = await supabase
    .from('courses')
    .update(updates)
    .eq('id', id)
    .select('*, category:category_id(*)')
    .single();

  if (error) {
    console.error(`Error updating course with id ${id}:`, error);
    throw error;
  }

  // Cập nhật cache
  clearCourseCache(CACHE_KEYS.COURSES);
  
  return data;
};

// Xóa khóa học
export const deleteCourse = async (id: string): Promise<Course | null> => {
  // Lấy thông tin khóa học để biết URL hình ảnh
  const course = await getCourseById(id);

  // Xóa nội dung khóa học trước
  try {
    const courseContent = await getCourseContent(id);
    if (courseContent) {
      await supabase
        .from('course_contents')
        .delete()
        .eq('id', courseContent.id);
    }
  } catch (error) {
    console.error(`Error deleting course content for course ${id}:`, error);
    // Không throw error ở đây, tiếp tục xóa khóa học
  }

  // Xóa khóa học
  const { error } = await supabase
    .from('courses')
    .delete()
    .eq('id', id);

  if (error) {
    console.error(`Error deleting course with id ${id}:`, error);
    throw error;
  }

  // Cập nhật cache
  clearCourseCache(CACHE_KEYS.COURSES);
  
  // Trả về thông tin khóa học để phía gọi API có thể xử lý xóa ảnh
  return course;
};

// Tạo nội dung khóa học
export const createCourseContent = async (content: Omit<CourseContent, 'created_at' | 'updated_at'>): Promise<CourseContent> => {
  console.log('Creating course content:', content);
  
  const now = new Date().toISOString();
  const newContent = {
    ...content,
    created_at: now,
    updated_at: now
  };
  
  const { data, error } = await supabase
    .from('course_contents')
    .insert(newContent)
    .select()
    .single();

  if (error) {
    console.error('Error creating course content:', error);
    throw error;
  }

  // Cập nhật cache
  setCourseContentCache(data, content.course_id);
  
  return data;
};

// Biến toàn cục để theo dõi request đang được thực hiện
let pendingContentUpdates: { [key: string]: { timestamp: number, promise: Promise<any> } } = {};

// Cập nhật nội dung khóa học
export const updateCourseContent = async (id: string, content: Partial<CourseContent>, isUpsert: boolean = false): Promise<CourseContent> => {
  console.log(`[updateCourseContent] Start updating content with id ${id}, isUpsert=${isUpsert}`);
  console.log(`[updateCourseContent] Content to update:`, content);
  
  const currentTime = Date.now();
  
  // Kiểm tra xem có request đang chờ cho ID này không và thời gian cách nhau dưới 1 giây
  if (pendingContentUpdates[id] && (currentTime - pendingContentUpdates[id].timestamp < 1000)) {
    console.log(`[updateCourseContent] Reusing pending request for content ${id}`);
    // Trả về promise đang pending để tránh tạo request mới
    return pendingContentUpdates[id].promise;
  }
  
  // Tạo promise mới và lưu vào pending
  const updatePromise = (async () => {
    // Kiểm tra xem nội dung khóa học đã tồn tại chưa
    if (isUpsert) {
      const { data: existingContent, error: checkError } = await supabase
        .from('course_contents')
        .select('id')
        .eq('id', id)
        .maybeSingle();
      
      console.log(`[updateCourseContent] Check existing content result:`, { existingContent, checkError });
      
      // Nếu nội dung không tồn tại và isUpsert = true, tạo mới
      if (!existingContent) {
        console.log(`[updateCourseContent] Content with id ${id} not found, creating new content (upsert)`);
        
        // Đảm bảo có course_id
        if (!content.course_id) {
          throw new Error('course_id is required for upsert');
        }
        
        const now = new Date().toISOString();
        const newContent = {
          id,
          course_id: content.course_id,
          content: content.content || {},
          created_at: now,
          updated_at: now
        };
        
        console.log('Creating new content with:', newContent);
        
        const { data, error } = await supabase
          .from('course_contents')
          .insert(newContent)
          .select()
          .single();
        
        if (error) {
          console.error(`Error creating course content during upsert with id ${id}:`, error);
          throw error;
        }
        
        // Cập nhật cache
        setCourseContentCache(data, data.course_id);
        
        return data;
      }
    }
    
    // Cập nhật nội dung khóa học nếu đã tồn tại hoặc không phải upsert
    const now = new Date().toISOString();
    const updates = {
      ...content,
      updated_at: now
    };
    
    const { data, error } = await supabase
      .from('course_contents')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error(`Error updating course content with id ${id}:`, error);
      throw error;
    }

    // Cập nhật cache
    setCourseContentCache(data, data.course_id);
    
    return data;
  })();
  
  // Lưu promise hiện tại
  pendingContentUpdates[id] = {
    timestamp: currentTime,
    promise: updatePromise
  };
  
  try {
    const result = await updatePromise;
    return result;
  } finally {
    // Xóa promies khỏi danh sách sau khi hoàn thành
    setTimeout(() => {
      if (pendingContentUpdates[id]?.timestamp === currentTime) {
        delete pendingContentUpdates[id];
      }
    }, 1000);
  }
};

// Xóa nội dung khóa học
export const deleteCourseContent = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('course_contents')
    .delete()
    .eq('id', id);

  if (error) {
    console.error(`Error deleting course content with id ${id}:`, error);
    throw error;
  }
};

// Lấy khóa học theo danh mục
export const getCoursesByCategory = async (categoryId: string): Promise<Course[]> => {
  const { data, error } = await supabase
    .from('courses')
    .select('*, category:category_id(*)')
    .eq('category_id', categoryId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error(`Error fetching courses for category ${categoryId}:`, error);
    throw error;
  }

  return data || [];
};

// Tìm kiếm khóa học
export const searchCourses = async (searchTerm: string): Promise<Course[]> => {
  const { data, error } = await supabase
    .from('courses')
    .select('*, category:category_id(*)')
    .or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)
    .order('created_at', { ascending: false });

  if (error) {
    console.error(`Error searching courses with term ${searchTerm}:`, error);
    throw error;
  }

  return data || [];
};

// Lấy danh sách danh mục cho khóa học
export const getCourseCategoriesFromCache = async (): Promise<Category[]> => {
  try {
    // Lấy tất cả danh mục từ categoryService
    const { getAllCategories } = await import('./categoryService');
    const allCategories = await getAllCategories();
    
    // Lọc danh mục theo loại nội dung COURSE
    return filterCategoriesByContentType(allCategories, ContentType.COURSE);
  } catch (error) {
    console.error('Error fetching course categories:', error);
    return [];
  }
};

// Lấy ID danh mục mặc định từ bảng categories
export const getDefaultCategoryId = async (): Promise<string> => {
  try {
    // Lấy danh mục đầu tiên từ bảng categories
    const { data, error } = await supabase
      .from('categories')
      .select('id')
      .or('content_type.eq.all,content_type.eq.course')
      .limit(1)
      .single();

    if (error) {
      console.error('Error fetching default category:', error);
      // Fallback: Trả về một UUID hợp lệ cố định
      return '00000000-0000-0000-0000-000000000000';
    }

    return data.id;
  } catch (error) {
    console.error('Error in getDefaultCategoryId:', error);
    // Fallback: Trả về một UUID hợp lệ cố định
    return '00000000-0000-0000-0000-000000000000';
  }
};

// Lấy khóa học theo category slug (sử dụng bảng categories để lấy category_id từ slug)
export const getCoursesByCategorySlug = async (categorySlug: string): Promise<Course[]> => {
  try {
    // Lấy category_id từ slug
    const { data: categoryData, error: categoryError } = await supabase
      .from('categories')
      .select('id')
      .eq('slug', categorySlug)
      .single();
    
    if (categoryError || !categoryData) {
      console.error(`Error fetching category by slug ${categorySlug}:`, categoryError);
      return [];
    }
    
    // Lấy khóa học bằng category_id
    const { data, error } = await supabase
      .from('courses')
      .select('*, category:category_id(*)')
      .eq('category_id', categoryData.id)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error(`Error fetching courses for category id ${categoryData.id}:`, error);
      return [];
    }
    
    return data || [];
  } catch (error) {
    console.error(`Error in getCoursesByCategorySlug for ${categorySlug}:`, error);
    return [];
  }
}; 